import pluralize from 'pluralize';

import { GalleryItemType } from '~/components/Gallery/types';

export type SelectedItemForSummary = {
  type?: string;
  selectedBoard?: { id: string; name: string };
};

export type BuildSelectedItemsSummaryParams = {
  selectedItems: SelectedItemForSummary[];
  crossLocationSelectionEnabled: boolean;
  selectAll: boolean;
  selectAllTotalCount?: number | null;
  omitBoardName?: boolean;
};

export type BuildSelectedItemsSummaryResult = {
  summary: string;
  countLabel: string;
  scopeLabel?: string;
};

export const buildSelectedItemsSummary = ({
  selectedItems,
  crossLocationSelectionEnabled,
  selectAll,
  selectAllTotalCount,
  omitBoardName,
}: BuildSelectedItemsSummaryParams): BuildSelectedItemsSummaryResult => {
  if (selectAll && selectAllTotalCount != null) {
    const countLabel =
      selectAllTotalCount === 1
        ? 'All items'
        : `All ${selectAllTotalCount.toLocaleString()} ${pluralize('item', selectAllTotalCount)}`;

    return { summary: `${countLabel} selected`, countLabel };
  }

  const hasOnlyCanvasSelected =
    selectedItems.length > 0 && selectedItems.every((item) => item.type === GalleryItemType.canvas);
  const canvasLabel = selectedItems.length === 1 ? 'Canvas' : 'Canvases';
  const itemLabel = hasOnlyCanvasSelected ? canvasLabel : pluralize('item', selectedItems.length);
  const countLabel = `${selectedItems.length} ${itemLabel}`;

  if (!crossLocationSelectionEnabled) {
    return { summary: `${countLabel} selected`, countLabel };
  }

  const itemsWithUndefinedSelectedBoard = selectedItems.filter((item) => item.selectedBoard === undefined);
  const itemsWithDefinedSelectedBoard = selectedItems.filter((item) => item.selectedBoard !== undefined);
  const boardNameLookup = new Map(
    itemsWithDefinedSelectedBoard.map((item) => [item.selectedBoard?.id, item.selectedBoard?.name]),
  );
  const distinctBoardIds = new Set(
    itemsWithDefinedSelectedBoard
      .filter((item) => item.selectedBoard !== undefined)
      .map((item) => item.selectedBoard?.id),
  );

  if (itemsWithUndefinedSelectedBoard.length > 0) {
    if (distinctBoardIds.size > 0) {
      return {
        summary: `${countLabel} selected across multiple locations`,
        countLabel,
        scopeLabel: 'Multiple locations',
      };
    }

    return { summary: `${countLabel} selected`, countLabel };
  }

  if (distinctBoardIds.size === 1) {
    const boardId = distinctBoardIds.values().next().value;
    const boardName = boardNameLookup.get(boardId);

    if (omitBoardName) {
      return { summary: `${countLabel} selected`, countLabel };
    }

    return {
      summary: `${countLabel} selected ${boardName ? `from ${boardName}` : ''}`,
      countLabel,
      scopeLabel: boardName,
    };
  }

  if (distinctBoardIds.size > 1) {
    return {
      summary: `${countLabel} selected across ${distinctBoardIds.size} boards`,
      countLabel,
      scopeLabel: `${distinctBoardIds.size} boards`,
    };
  }

  return { summary: `${countLabel} selected`, countLabel };
};
